import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {ImageGenerator, ImageNodeGenerator, MaterialGraph} from "@cm/material-nodes"
import {BsdfPrincipled, Color, Mapping, materialConnection, MixRgb, NormalMap, UvMap} from "@cm/material-nodes"
import {MaterialSlotType as TransformMaterialSlotType} from "#template/nodes/types"

export {TransformMaterialSlotType}

const TD = TypeDescriptors

type TransformMaterialSlotDescriptorType = {
    sceneManager: InletDescriptor<ISceneManager>
    material: InletDescriptor<MaterialGraph>
    size: InletDescriptor<[number, number]>
    image: InletDescriptor<ImageGenerator | null>
    slot: InletDescriptor<TransformMaterialSlotType>
    blendAmount: InletDescriptor<number>
    uvChannel: InletDescriptor<number>
    outputMaterial: OutletDescriptor<MaterialGraph>
}

const transformMaterialSlotDescriptor: TransformMaterialSlotDescriptorType = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    material: TD.inlet(TD.MaterialGraph),
    size: TD.inlet(TD.Tuple<[number, number]>()),
    image: TD.inlet(TD.Nullable(TD.ImageGenerator)),
    slot: TD.inlet(TD.String) as InletDescriptor<TransformMaterialSlotType>,
    blendAmount: TD.inlet(TD.Number),
    uvChannel: TD.inlet(TD.Number),
    outputMaterial: TD.outlet(TD.MaterialGraph),
}

export class TransformMaterialSlot implements NodeClassImpl<TransformMaterialSlotDescriptorType, typeof TransformMaterialSlot> {
    static descriptor = transformMaterialSlotDescriptor
    static uniqueName = "TransformMaterialSlot"
    sceneManager!: Inlet<ISceneManager>
    material!: Inlet<MaterialGraph>
    size!: Inlet<[number, number]>
    image!: Inlet<ImageGenerator | null>
    slot!: Inlet<TransformMaterialSlotType>
    blendAmount!: Inlet<number>
    uvChannel!: Inlet<number>
    outputMaterial!: Outlet<MaterialGraph>

    run() {
        const material = this.material
        const image = this.image
        const size = this.size
        const slot = this.slot
        const blendAmount = this.blendAmount
        const uvChannel = this.uvChannel
        const sceneManager = this.sceneManager
        if (
            material === NotReady ||
            image === NotReady ||
            size === NotReady ||
            slot === NotReady ||
            blendAmount === NotReady ||
            uvChannel === NotReady ||
            sceneManager === NotReady
        ) {
            this.outputMaterial.emitIfChanged(NotReady)
            return
        }

        this.outputMaterial.emitIfChanged(transformMaterialSlotFn(material, image?.imageNode, size, slot, blendAmount, uvChannel))
    }
}

function transformMaterialSlotFn(
    originalGraph: MaterialGraph,
    image: ImageNodeGenerator | undefined,
    size: [number, number],
    slot: TransformMaterialSlotType,
    blendAmount: number,
    uvChannel: number,
): MaterialGraph {
    if (blendAmount <= 0.0 || !image) return originalGraph

    const graph = originalGraph.clone({cloneSubNode: () => true})

    const uv = materialConnection(
        new UvMap({
            parameters: {
                inputs: {},
                settings: {uvMapIndex: uvChannel},
            },
        }),
        "uv",
    )

    const vec = materialConnection(
        new Mapping({
            parameters: {
                inputs: {vector: uv},
                settings: {vectorType: "TEXTURE", scale: {x: size[0], y: size[1], z: 1}},
            },
        }),
        "vector",
    )

    const imgNodeData = image.generator({uv: vec, extension: "REPEAT", interpolation: "Linear", projection: "FLAT", size})

    for (const bsdf of graph.nodes) {
        if (bsdf instanceof BsdfPrincipled) {
            if (slot === TransformMaterialSlotType.Normal) {
                const input = bsdf.parameters.inputs.normal

                if (input) {
                    const patchNormalMap = (normalMap: NormalMap) => {
                        const originalColor = normalMap.parameters.inputs.color
                        if (!originalColor || blendAmount >= 1.0) {
                            normalMap.updateInput("color", imgNodeData.color)
                        } else {
                            const mix = materialConnection(
                                new MixRgb({
                                    parameters: {
                                        inputs: {color1: originalColor, color2: imgNodeData.color},
                                        settings: {
                                            blendType: "MIX",
                                            fac: blendAmount,
                                        },
                                    },
                                }),
                                "color",
                            )

                            normalMap.updateInput("color", mix)
                        }
                    }

                    const node = input.source
                    if (node instanceof NormalMap) {
                        patchNormalMap(node)
                    } else {
                        throw new Error("Expected normal map to be a NormalMap")
                    }
                } else {
                    const mix = materialConnection(
                        new MixRgb({
                            parameters: {
                                inputs: {color2: imgNodeData.color},
                                settings: {
                                    color1: {r: 0.5, g: 0.5, b: 1.0},
                                    blendType: "MIX",
                                    fac: blendAmount,
                                },
                            },
                        }),
                        "color",
                    )

                    const normalMap = materialConnection(
                        new NormalMap({
                            parameters: {
                                inputs: {color: mix},
                                settings: {strength: 1},
                            },
                        }),
                        "normal",
                    )

                    bsdf.updateInput("normal", normalMap)
                }
            } else {
                const getNewRgbInput = () => {
                    if (blendAmount >= 1.0) {
                        return imgNodeData.color
                    } else {
                        const getExistingInput = () => {
                            const input = bsdf.parameters.inputs[slot]
                            const parameter = bsdf.parameters.settings[slot] ?? {r: 0, g: 0, b: 0}

                            if (input) return input
                            else {
                                return materialConnection(
                                    new Color({
                                        parameters: {
                                            inputs: {},
                                            settings: {color: typeof parameter === "number" ? {r: parameter, g: parameter, b: parameter} : parameter},
                                        },
                                    }),
                                    "color",
                                )
                            }
                        }

                        const mix = materialConnection(
                            new MixRgb({
                                parameters: {
                                    inputs: {color1: getExistingInput(), color2: imgNodeData.color},
                                    settings: {
                                        blendType: "MIX",
                                        fac: blendAmount,
                                    },
                                },
                            }),
                            "color",
                        )

                        return mix
                    }
                }

                bsdf.updateInput(slot, getNewRgbInput())
            }
        }
    }

    return graph
}
